import {Component, TemplateRef, ViewChild} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {Router} from '@angular/router';
import {RoleMappingService} from './portal/shared/services/role-mapping.service';
import { AlertMessage, EventMessageBusService } from './public/services/event-message-bus.service';
import { StorageService } from './account/services/storage.service';
import { AuthService } from './account/services/auth.service';
import { decodeJwtPayload } from './account/utils/jwt-utils';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AidaWidgetService } from './portal/shared/services/aida-widget.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
    public isLoggedIn = false;

    public messages: AlertMessage[] = [];
    @ViewChild('saveErrorTpl', { static: true })
    saveErrorTpl!: TemplateRef<void>;

    constructor(
        private storageService: StorageService,
        private router: Router,
        private messageBus: EventMessageBusService,
        private modal: NzModalService,
        private authService: AuthService, // Add AuthService to constructor
        private roleMappingService: RoleMappingService, // Add RoleMappingService to constructor
        private aidaWidgetService: AidaWidgetService) {

        this.messageBus.newMessagePosted$.subscribe((message => {
            this.messages.push(message);
            if (message.showAdditionalModal == true) {
                this.errorOnSave();
            }
        }));
        this.messageBus.messageTimeout$.subscribe(({id}) => {
            const index = this.messages.findIndex((msg) => msg.id === id);
            this.messages.splice(index, 1);
        });
    }

    errorOnSave(): void {
        this.modal.error({
            nzTitle: 'Error',
            nzContent: this.saveErrorTpl
        });
    }

    public async ngOnInit() {
        // Feature flags are already loaded by APP_INITIALIZER
        this.aidaWidgetService.load();

        // Initialize role mapping service early for logged in users
        this.isLoggedIn = this.storageService.isLoggedIn;
        if (this.isLoggedIn) {
            // Wait for roles to be loaded before continuing
            try {
                await firstValueFrom(this.roleMappingService.loadRoles());
            } catch (err) {
                console.error('Failed to initialize role mapping:', err);
            }
        }

        // Add token refresh logic
        if (this.isLoggedIn) {
            const token = this.storageService.getToken();

            if (token) {
                // JWT payloads are base64url-encoded; decode via the shared helper (browser atob
                // cannot parse base64url and multi-byte UTF-8 name claims directly).
                const payload = decodeJwtPayload(token);

                if (payload && typeof payload.exp === 'number') {
                    const expires = new Date(payload.exp * 1000);

                    // If token expires soon (less than 5 minutes), refresh it
                    if (expires.getTime() - Date.now() < 5 * 60 * 1000) {
                        this.authService.refreshToken().subscribe({
                            error: err => {
                                console.error('Failed to refresh token on startup:', err);
                                this.authService.logout();
                                // this.router.navigate(['/account/login']);
                            }
                        });
                    } else {
                        // Otherwise just start the refresh timer
                        this.authService.startRefreshTokenTimer({ access_token: token });
                    }
                } else {
                    // A payload we cannot decode must NOT force a logout (this was the cause of the
                    // post-login logout loop for users with non-Latin names). Keep the session and
                    // let the refresh timer / interceptor manage expiry non-destructively.
                    console.warn('Could not decode token payload on startup; keeping session and starting refresh timer');
                    this.authService.startRefreshTokenTimer({ access_token: token });
                }
            }
        }
    }

    public login() {
        this.router.navigate(['/account/login']);
    }

    public home() {
        this.router.navigate(['/account/home']);
    }
}
